import { KEY } from '@constants/key';
import { useLocalization } from '@lib/localization';
import {
  Box, Button, ButtonBase, Dialog, Divider, IconButton, LinearProgress, Stack, Tooltip, Typography,
} from '@mui/material';
import { buttonStyles } from '@pages/CustomPoiTypes.styles';
import {
  candidateSelectedStyles, checkCircleSelectedStyles, checkCircleStyles, dialogCloseStyles, dialogFooterStyles,
  dialogHeaderStyles, dialogPaperStyles,
} from '@pages/Teams.styles';
import {
  isReportDesignMissing, reportLayouts, useLazyGetInspectionReportQuery, type IInspection, type ReportLayout,
  type ReportRendering,
} from '@store/api/inspectionEndpoints';
import { isBrandableLayout } from '@store/api/reportBrandingEndpoints';
import {
  groupByOwner, ownerLabel, reportDesignApiSlice, useGetReportDesignsQuery,
} from '@store/api/reportDesignEndpoints';
import { isServerError } from '@store/error';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { showSnackbar } from '@store/reducers/snackbarSlice';
import { ChartPie, Check, DownloadIcon, FileText, LayoutTemplate, Rows3, X } from 'lucide-react';
import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  controlStyles, designsFailedStyles, designsNoteStyles, optionIconStyles, optionListStyles, optionNoteStyles,
  optionStyles, ownerHeadingStyles,
} from './InspectionReportDownload.styles';

/*
 * What the reader last drew a report with is kept in this browser, under `KEY.REPORT_LAYOUT`,
 * and is what the dialog opens on. Deliberately there and not among the account's
 * preferences. The value is a layout's id, or `design:` and a design's id.
 */
const CLASSIC: ReportLayout = 'classic';
const DESIGN_PREFIX = 'design:';

/** What a choice asks the API for. Anything that is not one is the classic layout. */
const renderingOf = (choice: string | null): ReportRendering => {
  if (choice?.startsWith(DESIGN_PREFIX) && choice.length > DESIGN_PREFIX.length) {
    return { designId: choice.slice(DESIGN_PREFIX.length) };
  }

  return { layout: reportLayouts.find((layout) => layout === choice) ?? CLASSIC };
};

/** Where an arrow key takes the choice: on to the next option, or back to the one before. */
const arrowSteps: Partial<Record<string, number>> = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };

interface LayoutChooserProps {
  /** The id of the title, which is what the dialog and its options are named by. */
  titleId: string;
  /** Whether the dialog is open. MUI goes on drawing its inside while it fades out. */
  open: boolean;
  downloading: boolean;
  onClose: () => void;
  /** `remember` is whether the choice is to be what the dialog opens on from now on. */
  onDownload: (choice: string, remember: boolean) => void;
}

/**
 * The inside of the layout dialog. A component of its own so that it exists only while the
 * dialog is open: a list of inspections draws one download control per row, and none of
 * them asks for the designs before its dialog is opened.
 */
const LayoutChooser: React.FC<LayoutChooserProps> = ({ titleId, open, downloading, onClose, onDownload }) => {
  const { t } = useLocalization();
  const sessionTeams = useAppSelector((state) => state.auth.user?.teams);
  const defaultTeamId = useAppSelector((state) => state.auth.user?.defaultTeam?.id);

  const { data: designs, isError, requestId } = useGetReportDesignsQuery();

  // The designs could not be read, and no earlier read left a list to offer: the request
  // limiter refused, the server was restarting, the connection dropped. A failure the cache
  // still holds from a dialog opened earlier, on this inspection or another, is not this
  // one's: the designs are asked for again as the dialog opens, and until that read is
  // answered they are on their way like any others.
  const [failedBefore] = useState(() => (isError ? requestId : undefined));
  const loadFailed = isError && designs === undefined && requestId !== failedBefore;
  const loading = designs === undefined && !loadFailed;

  const designsNoteId = useId();
  const options = useRef<HTMLDivElement>(null);

  // Starts on what the reader chose last, which is the classic layout until they choose.
  const [picked, setPicked] = useState(() => localStorage.getItem(KEY.REPORT_LAYOUT) ?? CLASSIC);

  // The organization's designs and those of the reader's own teams. Someone who can read
  // every team's designs is still offered only these.
  const groups = useMemo(() => {
    const ownTeams = new Set(sessionTeams?.map((team) => team.id));

    return groupByOwner((designs ?? []).filter((design) => design.teamId === null || ownTeams.has(design.teamId)));
  }, [designs, sessionTeams]);

  const offered: string[] = [
    ...reportLayouts,
    ...groups.flatMap((group) => group.designs.map((design) => DESIGN_PREFIX + design._id)),
  ];

  // A remembered design that is not on offer gives way to the classic layout, which is what
  // downloading with it would come to. Only once the designs are in or are known not to
  // come: while they are on their way nothing says whether the design is on offer, and
  // Download waits for them.
  const selected = loading || offered.includes(picked) ? picked : CLASSIC;
  const onOffer = offered.includes(selected);

  // A design the list no longer has is gone, and the layout takes its place for good once a
  // report is downloaded with it. Where the list could not be read the design is most likely
  // still there, and the layout only stands in for it: the reader did not choose it, so
  // downloading with it leaves the remembered design as it is, and the dialog opens on that
  // design again once its list can be read.
  const standsIn = loadFailed && selected !== picked;

  // The options are one stop for the Tab key, as a group of radio buttons is: the chosen
  // one, or the first while the choice is a design that is not on offer.
  const tabStop = onOffer ? selected : offered[0];

  // The dialog opens with the keyboard on the chosen option, which is where a group of radio
  // buttons is entered and what makes a screen reader say the choice. Left alone, MUI puts
  // it on the dialog as a whole, and only when nothing in the dialog has it. A remembered
  // design is on offer once the designs are in; by then the reader may have gone to a
  // control of the dialog themselves, and is left there. Only while the dialog is open:
  // closed, it is drawn until it has faded out, with the keyboard already back on the
  // download button, and designs that arrive in that moment must not take the keyboard into
  // a dialog that is about to go. One opened again before it has gone is entered like any.
  useEffect(() => {
    if (!open || !onOffer) return;

    const chosen = options.current?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]');

    if (chosen && !chosen.closest('[role="dialog"]')?.contains(document.activeElement)) chosen.focus();
  }, [open, onOffer]);

  // The arrow keys move the choice and the keyboard with it, as among radio buttons, and go
  // round from the last option to the first. The options stand in the order of `offered`.
  const moveChoice = (event: React.KeyboardEvent<HTMLElement>) => {
    const step = arrowSteps[event.key];

    // With a modifier the key is the browser's: Alt and the left arrow is its way back.
    if (step === undefined || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

    const radios = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]'));
    const from = radios.indexOf(event.target as HTMLElement);

    if (from < 0) return;

    const to = (from + step + radios.length) % radios.length;

    // Not the browser's scrolling of the list as well: the option the keyboard goes to is
    // brought into view by that alone.
    event.preventDefault();
    setPicked(offered[to]);
    radios[to].focus();
  };

  const layouts: Record<ReportLayout, { icon: React.ReactNode; name: string; description: string }> = {
    classic: {
      icon: <FileText size={20} />,
      name: t('Report_Layout_Classic'),
      description: t('Report_Layout_Classic_Desc'),
    },
    compact: {
      icon: <Rows3 size={20} />,
      name: t('Report_Layout_Compact'),
      description: t('Report_Layout_Compact_Desc'),
    },
    executive: {
      icon: <ChartPie size={20} />,
      name: t('Report_Layout_Executive'),
      description: t('Report_Layout_Executive_Desc'),
    },
  };

  // A layout that takes a brand is printed with the organization's, and over it the one of
  // the reader's default team. Someone in one team has no other brand to expect; someone in
  // several is told which team's it will be, since they do not choose it here. A design
  // takes that brand's logo and name where it sets none of its own. The list of the designs
  // does not say which of them do, so one line over the designs says it for them all.
  const brandTeam = (sessionTeams?.length ?? 0) > 1
    ? sessionTeams?.find((team) => team.id === defaultTeamId)?.name
    : undefined;

  const option = (
    value: string, icon: React.ReactNode, name: string, description: string | null, note: string | null = null,
    describedBy?: string,
  ) => (
    <ButtonBase
      key={value}
      role="radio"
      aria-checked={value === selected}
      aria-describedby={describedBy}
      tabIndex={value === tabStop ? 0 : -1}
      onClick={() => setPicked(value)}
      sx={value === selected ? [optionStyles, candidateSelectedStyles] : optionStyles}
    >
      <Box sx={optionIconStyles}>{icon}</Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{name}</Typography>
        {description && <Typography color="text.secondary" sx={{ fontSize: 13 }}>{description}</Typography>}
        {note && <Typography sx={optionNoteStyles}>{note}</Typography>}
      </Box>
      <Box sx={value === selected ? [checkCircleStyles, checkCircleSelectedStyles] : checkCircleStyles}>
        {value === selected && <Check size={14} />}
      </Box>
    </ButtonBase>
  );

  return (
    <>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={dialogHeaderStyles}>
        <Box>
          <Typography id={titleId} sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.4 }}>
            {t('Report_ChooseLayout')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t('Report_ChooseLayout_Hint')}
          </Typography>
        </Box>
        <IconButton size="small" aria-label={t('Common_Close')} onClick={onClose} sx={dialogCloseStyles}>
          <X size={18} />
        </IconButton>
      </Stack>

      <Divider />

      <Box ref={options} role="radiogroup" aria-labelledby={titleId} onKeyDown={moveChoice} sx={optionListStyles}>
        {reportLayouts.map((layout) => option(
          layout, layouts[layout].icon, layouts[layout].name, layouts[layout].description,
          brandTeam && isBrandableLayout(layout) ? t('Report_Layout_Brand', [brandTeam]) : null,
        ))}

        {loading && <LinearProgress />}

        {/*
          Said where the designs would be; the layouts above need no list and stay on offer.
          The place of the line is drawn from the start and takes no room while it is empty:
          a screen reader says what comes to stand in a status, and not always a status that
          arrives with its text in it.
        */}
        <Box role="status">
          {loadFailed && <Typography sx={designsFailedStyles}>{t('ReportDesign_ListLoadFailed')}</Typography>}
        </Box>

        {brandTeam && groups.length > 0 && (
          <Typography id={designsNoteId} sx={designsNoteStyles}>{t('Report_Design_Brand', [brandTeam])}</Typography>
        )}

        {groups.map((group) => (
          <Fragment key={group.teamId ?? 'organization'}>
            <Typography sx={ownerHeadingStyles}>{ownerLabel(group)}</Typography>
            {group.designs.map((design) => option(
              DESIGN_PREFIX + design._id, <LayoutTemplate size={20} />, design.name, design.description, null,
              // The line over the designs is about each of them, and is read out with each.
              brandTeam ? designsNoteId : undefined,
            ))}
          </Fragment>
        ))}
      </Box>

      <Divider />

      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={dialogFooterStyles}>
        <Button color="inherit" size="small" onClick={onClose} sx={buttonStyles}>
          {t('Common_Cancel')}
        </Button>
        <Button
          variant="contained"
          size="small"
          disableElevation
          startIcon={<DownloadIcon size={16} />}
          onClick={() => onDownload(selected, !standsIn)}
          sx={buttonStyles}
          disabled={downloading || !onOffer}
        >
          {t('Report_Download', ['(.pdf)'])}
        </Button>
      </Stack>
    </>
  );
};

interface InspectionReportDownloadProps {
  inspection: IInspection;
  projectId: string;
  projectName: string;
}

/**
 * Downloads an inspection's PDF report. The button opens the layouts and the report designs
 * to choose from, on what the reader chose last, and the report is downloaded from there:
 * what a report is drawn with is chosen, or confirmed, every time. Downloading is what makes
 * a choice the remembered one, unless it is the layout that only stands in for a remembered
 * design whose list could not be read.
 *
 * It was two buttons at first: one that downloaded at once with the last choice, and an
 * arrow beside it for the choice.
 */
export const InspectionReportDownload: React.FC<InspectionReportDownloadProps> = ({
  inspection, projectId, projectName }) => {
  const { t, currentLanguage } = useLocalization();
  const dispatch = useAppDispatch();
  const [fetchInspectionReport, { isFetching }] = useLazyGetInspectionReportQuery();
  const [chooserOpen, setChooserOpen] = useState(false);
  // What the dialog is named by: MUI's own title component would carry an id by itself, the
  // title drawn here has to be given one.
  const titleId = useId();

  /** Asks for the report and hands it to the browser as a file. Answers whether one arrived. */
  const download = async (rendering: ReportRendering): Promise<boolean> => {
    const request = { projectId, inspectionId: inspection.info.id, locale: currentLanguage, ...rendering };
    const result = await fetchInspectionReport(request);

    if (isReportDesignMissing(request, result)) {
      // The design has been deleted since the dialog listed it, or is no longer the reader's
      // to use. The report still comes, in the classic layout, and the choice is forgotten
      // once it has: an inspection that is gone or not theirs is refused the same way, and
      // that says nothing about the design.
      const delivered = await download({ layout: CLASSIC });

      if (delivered) {
        localStorage.removeItem(KEY.REPORT_LAYOUT);
        dispatch(reportDesignApiSlice.util.invalidateTags([{ type: 'ReportDesign', id: 'LIST' }]));
        dispatch(showSnackbar({ severity: 'info', message: t('ReportDesign_Gone') }));
      }

      return delivered;
    }

    // A request that failed still carries the address an earlier one left in the cache.
    if (result.isError || !result.data) {
      // A refusal in the API's own words was shown where the request is made, and the 401
      // of a session that has ended is the API's too. What is left has no words of its own:
      // the request limiter's plain text, a gateway's page while the server restarts, a
      // connection that dropped. The dialog stays open, and without this line in silence.
      if (!isServerError(result)) {
        dispatch(showSnackbar({ severity: 'error', message: t('Report_Download_Failed') }));
      }

      return false;
    }

    const link = document.createElement('a');
    link.href = result.data;
    link.download = `${projectName} - ${inspection.info.name}.pdf`;
    link.click();

    URL.revokeObjectURL(result.data);

    return true;
  };

  const downloadChosen = async (choice: string, remember: boolean) => {
    if (remember) localStorage.setItem(KEY.REPORT_LAYOUT, choice);

    if (await download(renderingOf(choice))) setChooserOpen(false);
  };

  // In a list the control sits on a row that opens the inspection, and no click on it is
  // that: not one on a button, a disabled one included, and not one in the dialog, whose
  // clicks travel up through the components that opened it wherever it is drawn.
  return (
    <Box sx={controlStyles} onClick={(event) => event.stopPropagation()}>
      <Tooltip title={t('Report_Download', ['(.pdf)'])}>
        <span>
          <IconButton
            onClick={() => setChooserOpen(true)}
            disabled={isFetching}
            // Named here as well: the tooltip names the wrapper it stands on, not the button in it.
            aria-label={t('Report_Download', ['(.pdf)'])}
            aria-haspopup="dialog"
          >
            <DownloadIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        aria-labelledby={titleId}
        fullWidth
        maxWidth="sm"
        slotProps={{ paper: { sx: { ...dialogPaperStyles, maxWidth: 480 } } }}
      >
        <LayoutChooser
          titleId={titleId}
          open={chooserOpen}
          downloading={isFetching}
          onClose={() => setChooserOpen(false)}
          onDownload={downloadChosen}
        />
      </Dialog>
    </Box>
  );
};
